import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, User } from "lucide-react";

const TESTER_SOFT_CAP = 10;

export default function TesterAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.put(`/admin/users/${u.id}/tester`, { is_tester: !u.is_tester });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(data.is_tester ? `${data.name || data.email} is now a tester.` : `Tester removed from ${data.name || data.email}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const testerCount = users.filter((u) => u.is_tester).length;
  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return u.email.toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
  });

  return (
    <section className="space-y-8" data-testid="tester-admin">
      <div className="grid md:grid-cols-12 gap-6 border-t border-b border-border">
        <div className="md:col-span-8 p-8">
          <div className="label-eyebrow mb-4">Pre-launch testers</div>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
            Give trusted people <em className="italic text-[hsl(var(--primary))]">early access</em>.
          </h2>
          <p className="text-stone-600 leading-relaxed max-w-2xl">
            Flag any account as a tester and they'll skip the "Program begins December 1, 2026" countdown — they see the full December practice as if launch had happened. Toggle off any time.
          </p>
        </div>
        <div className="md:col-span-4 p-8 border-l border-border">
          <div className="label-eyebrow mb-2">Testers active</div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-6xl" data-testid="tester-count">{testerCount}</span>
            <span className="text-stone-500">/ {TESTER_SOFT_CAP} soft cap</span>
          </div>
          {testerCount > TESTER_SOFT_CAP && (
            <p className="text-xs text-[hsl(var(--primary))] mt-3 italic" data-testid="tester-over-cap">
              You're above your soft cap of {TESTER_SOFT_CAP}. No hard limit — just a gentle nudge.
            </p>
          )}
        </div>
      </div>

      <div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="tester-search"
          placeholder="Search by name or email…"
          className="w-full max-w-md bg-transparent border-0 border-b border-border py-2 no-shadow-focus text-lg"
        />
      </div>

      {loading ? (
        <div className="label-eyebrow">Loading users…</div>
      ) : (
        <table className="w-full text-sm border-t border-border" data-testid="tester-table">
          <thead>
            <tr className="text-left text-stone-500">
              <th className="py-3 label-eyebrow">Member</th>
              <th className="py-3 label-eyebrow">Role</th>
              <th className="py-3 label-eyebrow">Joined</th>
              <th className="py-3 label-eyebrow text-right">Tester</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`tester-row-${u.id}`}>
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-stone-400" strokeWidth={1.25} />
                    <div>
                      <div className="font-serif text-lg leading-tight">{u.name || <em className="italic text-stone-400">no name</em>}</div>
                      <div className="text-xs text-stone-500">{u.email}</div>
                      {u.founding_member && <span className="inline-flex mt-1 text-[10px] uppercase tracking-widest text-[hsl(var(--primary))]">✦ Founding</span>}
                    </div>
                  </div>
                </td>
                <td className="py-4 text-stone-500">
                  {u.role === "admin" ? <span className="text-[hsl(var(--primary))] font-medium">Admin</span> : "Member"}
                </td>
                <td className="py-4 text-stone-500">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="py-4 text-right">
                  {u.role === "admin" ? (
                    <span className="text-xs text-stone-400 italic">Admin — always on</span>
                  ) : (
                    <button
                      onClick={() => toggle(u)}
                      disabled={busyId === u.id}
                      data-testid={`tester-toggle-${u.id}`}
                      aria-pressed={u.is_tester}
                      className={`relative w-11 h-6 border transition-colors disabled:opacity-50 ${u.is_tester ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))]" : "bg-[hsl(var(--background))] border-border"}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${u.is_tester ? "left-6" : "left-0.5"}`} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
